/**
 * LeadershipDirectory — the leaders on the organisation tree, browsable.
 * Photo, title, name, where they sit and how to reach them. Expand a card for
 * the leader's profile: a short bio and a photo gallery, so the directory
 * doubles as an About page.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Search, Mail, Phone, Users, ChevronDown, ChevronUp, ImagePlus, Trash2, Pencil, Loader2,
  ExternalLink, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_FILTERS = [
  { key: '', label: 'Everyone' },
  { key: 'global_ministry', label: 'Global' },
  { key: 'country', label: 'Countries' },
  { key: 'province', label: 'Provinces' },
  { key: 'district', label: 'Districts' },
  { key: 'assembly', label: 'Assemblies' },
];

const Initials = ({ name, className }) => (
  <span className={`bg-slate-100 text-slate-500 font-semibold grid place-items-center shrink-0 ${className}`}>
    {(name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
  </span>
);

const LeaderProfile = ({ leader, onLightbox }) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState(leader.leader_bio || '');
  const [uploading, setUploading] = useState(false);
  const orgId = leader.organisation_id;
  const gallery = leader.leader_photos || [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['org-leaders'] });
    queryClient.invalidateQueries({ queryKey: ['org-tree'] });
  };

  const saveBio = useMutation({
    mutationFn: () => api.put(`/admin/organisations/${orgId}`, { leader_bio: bio }),
    onSuccess: () => { toast.success('Bio saved'); setEditing(false); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not save the bio'),
  });

  const setFlag = useMutation({
    mutationFn: (patch) => api.put(`/admin/organisations/${orgId}`, patch),
    onSuccess: () => { toast.success('Publishing updated'); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not update publishing'),
  });

  const removePhoto = useMutation({
    mutationFn: (url) => api.delete(`/admin/organisations/${orgId}/leader-photos`,
      { params: { url } }),
    onSuccess: () => { toast.success('Photo removed'); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not remove the photo'),
  });

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    setUploading(true);
    try {
      await api.post(`/admin/organisations/${orgId}/leader-photos`, fd);
      toast.success('Gallery updated');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-t px-4 py-3 space-y-3 bg-slate-50/60"
         data-testid={`leader-profile-${orgId}`}>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">About</p>
          {!editing && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                    data-testid={`leader-bio-edit-${orgId}`}
                    onClick={() => { setBio(leader.leader_bio || ''); setEditing(true); }}>
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
        </div>
        {editing ? (
          <div className="space-y-2">
            <Textarea
              value={bio} onChange={(e) => setBio(e.target.value)} rows={4}
              data-testid={`leader-bio-input-${orgId}`}
              placeholder="A few sentences about this leader — calling, ministry focus, years of service…"
            />
            <div className="flex gap-2">
              <Button size="sm" disabled={saveBio.isPending}
                      data-testid={`leader-bio-save-${orgId}`}
                      onClick={() => saveBio.mutate()}>
                {saveBio.isPending ? 'Saving…' : 'Save bio'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-600 whitespace-pre-line"
             data-testid={`leader-bio-${orgId}`}>
            {leader.leader_bio || 'No bio yet — add one so the directory reads like an About page.'}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Gallery {gallery.length ? `· ${gallery.length}` : ''}
        </p>
        <div className="flex flex-wrap gap-2" data-testid={`leader-gallery-${orgId}`}>
          {gallery.map((url) => (
            <div key={url} className="relative group">
              <img src={url} alt="Leader"
                   onClick={() => onLightbox(url)}
                   className="h-16 w-16 rounded-md object-cover ring-1 ring-slate-200 cursor-zoom-in" />
              <button
                onClick={() => removePhoto.mutate(url)}
                data-testid={`leader-gallery-remove-${orgId}`}
                title="Remove photo"
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white ring-1 ring-slate-200 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-3 w-3 text-rose-500" />
              </button>
            </div>
          ))}
          <label className="h-16 w-16 rounded-md border-2 border-dashed border-slate-300 grid place-items-center cursor-pointer hover:border-indigo-400 transition-colors"
                 title="Add photos">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              : <ImagePlus className="h-4 w-4 text-slate-400" />}
            <input type="file" accept="image/*" multiple className="hidden"
                   data-testid={`leader-gallery-upload-${orgId}`}
                   onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
          </label>
        </div>
      </div>

      <div className="space-y-1.5 pt-1 border-t">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Public About page
        </p>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={leader.leader_public !== false}
                 data-testid={`leader-public-toggle-${orgId}`}
                 onChange={(e) => setFlag.mutate({ leader_public: e.target.checked })} />
          Show this leader on the public About page
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={!!leader.leader_contact_public}
                 data-testid={`leader-contact-toggle-${orgId}`}
                 onChange={(e) => setFlag.mutate({ leader_contact_public: e.target.checked })} />
          Publish their email and phone publicly
        </label>
      </div>
    </div>
  );
};

const AddLeaderDialog = ({ open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const [orgId, setOrgId] = useState('');
  const [form, setForm] = useState({ leader_name: '', leader_title: '', leader_bio: '' });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: orgs = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['org-flat-list'],
    queryFn: async () => (await api.get('/admin/organisations')).data,
    enabled: open,
  });

  const chosen = orgs.find((o) => o.id === orgId);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!orgId) { toast.error('Choose which body this leader serves'); return; }
    setBusy(true);
    try {
      await api.put(`/admin/organisations/${orgId}`, {
        ...form,
        leader_public: true,
        leader_contact_public: false,
      });
      if (photo) {
        const fd = new FormData();
        fd.append('file', photo);
        await api.post(`/admin/organisations/${orgId}/leader-photo`, fd);
      }
      toast.success(`${form.leader_name} added to the directory`);
      queryClient.invalidateQueries({ queryKey: ['org-leaders'] });
      queryClient.invalidateQueries({ queryKey: ['org-tree'] });
      setForm({ leader_name: '', leader_title: '', leader_bio: '' });
      setPhoto(null);
      setOrgId('');
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the leader');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"
                     data-testid="add-leader-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-indigo-600" /> Add a leader
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <Label>Which body do they serve?</Label>
            <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                    data-testid="add-leader-org" disabled={orgsLoading}
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm disabled:opacity-60">
              <option value="">
                {orgsLoading ? 'Loading organisations…' : '— choose an organisation —'}
              </option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} · {o.type_label || o.organisation_type}
                  {o.leader_name ? ' (has a leader)' : ''}
                </option>
              ))}
            </select>
            {chosen?.leader_name && (
              <p className="text-[11px] text-amber-700">
                {chosen.name} currently lists {chosen.leader_name} — saving will replace them.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="al-name">Full name</Label>
            <Input id="al-name" required value={form.leader_name} onChange={set('leader_name')}
                   data-testid="add-leader-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="al-title">Title</Label>
            <Input id="al-title" value={form.leader_title} onChange={set('leader_title')}
                   placeholder={chosen ? `e.g. ${chosen.leader_title || 'Resident Pastor'}` : 'e.g. Resident Pastor'}
                   data-testid="add-leader-title" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="al-photo">Photo</Label>
            <Input id="al-photo" type="file" accept="image/*"
                   onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                   data-testid="add-leader-photo" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="al-bio">Short bio</Label>
            <Textarea id="al-bio" rows={4} value={form.leader_bio} onChange={set('leader_bio')}
                      placeholder="Calling, ministry focus, years of service…"
                      data-testid="add-leader-bio" />
          </div>
          <p className="text-[11px] text-slate-500">
            They'll appear on the public About page straight away. Contact details stay
            private until you publish them on their profile card.
          </p>
          <Button type="submit" className="w-full" disabled={busy || orgsLoading}
                  data-testid="add-leader-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {busy ? 'Saving…' : 'Add to directory'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const LeadershipDirectory = () => {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [expanded, setExpanded] = useState({});
  const [lightbox, setLightbox] = useState('');
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['org-leaders', search, type],
    queryFn: async () => (await api.get('/admin/organisations/leaders', {
      params: { ...(search ? { search } : {}), ...(type ? { organisation_type: type } : {}) },
    })).data,
  });

  const leaders = data?.leaders || [];

  return (
    <div className="space-y-4" data-testid="leadership-directory">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1 min-w-0 sm:min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search leaders, titles or organisations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="leader-search-input"
          />
        </div>
        <div className="flex rounded-md border border-slate-200 overflow-hidden bg-white">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              onClick={() => setType(f.key)}
              data-testid={`leader-filter-${f.key || 'all'}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                type === f.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500 ml-auto" data-testid="leader-count">
          {leaders.length} leader{leaders.length === 1 ? '' : 's'}
        </span>
        <Button size="sm" className="h-8 text-xs" data-testid="add-leader-open"
                onClick={() => setAdding(true)}>
          <UserPlus className="h-3.5 w-3.5 mr-1" /> Add a leader
        </Button>
        <a href="/leadership" target="_blank" rel="noreferrer"
           data-testid="leader-about-page-link"
           className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1">
          <ExternalLink className="h-3 w-3" /> Public About page
        </a>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : leaders.length === 0 ? (
        <Card><CardContent className="py-12 text-center" data-testid="leadership-directory-empty">
          <Users className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No leaders recorded yet.</p>
          <p className="text-xs text-slate-500 mt-1">
            Open any organisation in the tree, add a leader in the Leadership section, and they will
            appear here.
          </p>
          <Button className="mt-4" data-testid="add-leader-open-empty"
                  onClick={() => setAdding(true)}>
            <UserPlus className="h-4 w-4 mr-1" /> Add your first leader
          </Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
          {leaders.map((l) => {
            const open = !!expanded[l.organisation_id];
            return (
              <Card key={l.organisation_id} className="hover:shadow-md transition-shadow overflow-hidden"
                    data-testid={`leader-card-${l.organisation_id}`}>
                <CardContent className="p-4 flex gap-3">
                  {l.leader_photo_url ? (
                    <img src={l.leader_photo_url} alt={l.leader_name}
                         className="h-14 w-14 rounded-full object-cover ring-1 ring-slate-200 shrink-0" />
                  ) : (
                    <Initials name={l.leader_name} className="h-14 w-14 rounded-full text-sm" />
                  )}
                  <div className="min-w-0 space-y-1 flex-1">
                    <p className="font-semibold text-slate-900 truncate">{l.leader_name}</p>
                    <p className="text-xs text-slate-500">{l.leader_title}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{l.type_label}</Badge>
                      {(l.countries || []).slice(0, 3).map((c) => (
                        <Badge key={c} variant="outline"
                               className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{c}</Badge>
                      ))}
                      {(l.leader_photos || []).length > 0 && (
                        <Badge variant="outline"
                               className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                          {l.leader_photos.length} photo{l.leader_photos.length === 1 ? '' : 's'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 truncate" title={l.path}>{l.path}</p>
                    {l.leader_bio && !open && (
                      <p className="text-xs text-slate-500 line-clamp-2"
                         data-testid={`leader-bio-preview-${l.organisation_id}`}>{l.leader_bio}</p>
                    )}
                    <div className="flex flex-col gap-0.5 pt-1">
                      {l.email && (
                        <a href={`mailto:${l.email}`}
                           className="text-xs text-indigo-600 hover:underline flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3" /> {l.email}
                        </a>
                      )}
                      {l.phone && (
                        <a href={`tel:${l.phone}`}
                           className="text-xs text-slate-600 hover:underline flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {l.phone}
                        </a>
                      )}
                    </div>
                    <button
                      onClick={() => setExpanded((p) => ({ ...p, [l.organisation_id]: !open }))}
                      data-testid={`leader-expand-${l.organisation_id}`}
                      className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1 pt-1"
                    >
                      {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      {open ? 'Hide profile' : 'View profile'}
                    </button>
                  </div>
                </CardContent>
                {open && <LeaderProfile leader={l} onLightbox={setLightbox} />}
              </Card>
            );
          })}
        </div>
      )}

      <AddLeaderDialog open={adding} onOpenChange={setAdding} />

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox('')}>
        <DialogContent className="max-w-3xl p-2" data-testid="leader-photo-lightbox">
          {lightbox && <img src={lightbox} alt="Leader" className="w-full rounded-md" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadershipDirectory;
